import { Box, type BoxProps } from "@mui/material";
import { keyframes, styled } from "@mui/material/styles";
import {
  type FC,
  type Key,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import { motion } from "@/theme";

const pageEnter = keyframes({
  from: {
    opacity: 0,
    transform: `translateY(${motion.distance.page})`,
  },
  to: {
    opacity: 1,
    transform: "translateY(0)",
  },
});

const contentEnter = keyframes({
  from: {
    opacity: 0,
    transform: `translateY(${motion.distance.step})`,
  },
  to: {
    opacity: 1,
    transform: "translateY(0)",
  },
});

const contentFade = keyframes({
  from: {
    opacity: 0,
  },
  to: {
    opacity: 1,
  },
});

export type MotionVariant = "settle" | "fade";

const contentAnimation = (variant: MotionVariant) =>
  `${variant === "fade" ? contentFade : contentEnter} ${
    motion.duration.content
  }ms ${motion.easing.enter} backwards`;

const PageTransitionRoot = styled(Box)(() => ({
  width: "100%",
  minWidth: 0,
  animation: `${pageEnter} ${motion.duration.page}ms ${motion.easing.enter} backwards`,
  "@media (prefers-reduced-motion: reduce)": {
    animation: "none",
  },
}));

type MotionPanelRootProps = {
  $active: boolean;
  $animate: boolean;
  $variant: MotionVariant;
};

const MotionPanelRoot = styled(Box, {
  shouldForwardProp: prop =>
    prop !== "$active" && prop !== "$animate" && prop !== "$variant",
})<MotionPanelRootProps>(({ $active, $animate, $variant }) => ({
  display: $active ? "block" : "none",
  animation: $animate ? contentAnimation($variant) : "none",
  "@media (prefers-reduced-motion: reduce)": {
    animation: "none",
  },
}));

type MotionContentRootProps = {
  $animate: boolean;
  $variant: MotionVariant;
};

const MotionContentRoot = styled(Box, {
  shouldForwardProp: prop => prop !== "$animate" && prop !== "$variant",
})<MotionContentRootProps>(({ $animate, $variant }) => ({
  animation: $animate ? contentAnimation($variant) : "none",
  "@media (prefers-reduced-motion: reduce)": {
    animation: "none",
  },
}));

export type PageTransitionProps = {
  children: ReactNode;
};

export const PageTransition: FC<PageTransitionProps> = ({ children }) => (
  <PageTransitionRoot>{children}</PageTransitionRoot>
);

export type MotionPanelProps = BoxProps & {
  active: boolean;
  variant?: MotionVariant;
};

export const MotionPanel: FC<MotionPanelProps> = ({
  active,
  children,
  variant = "settle",
  ...props
}) => {
  const [hasBeenInactive, setHasBeenInactive] = useState(!active);

  useEffect(() => {
    if (!active) setHasBeenInactive(true);
  }, [active]);

  return (
    <MotionPanelRoot
      {...props}
      $active={active}
      $animate={active && hasBeenInactive}
      $variant={variant}
      hidden={!active}
    >
      {children}
    </MotionPanelRoot>
  );
};

export type MotionContentProps = BoxProps & {
  animate?: boolean;
  variant?: MotionVariant;
};

export const MotionContent: FC<MotionContentProps> = ({
  animate = true,
  children,
  variant = "settle",
  ...props
}) => (
  <MotionContentRoot {...props} $animate={animate} $variant={variant}>
    {children}
  </MotionContentRoot>
);

export type MotionSwitchProps = Omit<MotionContentProps, "animate"> & {
  motionKey: Key;
};

/**
 * Animates semantic content changes without stacking an entry animation on
 * top of the route or dialog that introduced the first view.
 */
export const MotionSwitch: FC<MotionSwitchProps> = ({
  children,
  motionKey,
  ...props
}) => {
  const previousKey = useRef(motionKey);
  const hasSwitched = useRef(false);

  if (previousKey.current !== motionKey) {
    previousKey.current = motionKey;
    hasSwitched.current = true;
  }

  return (
    <MotionContent {...props} key={motionKey} animate={hasSwitched.current}>
      {children}
    </MotionContent>
  );
};
